import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMe } from '../../hooks/useMe.js';
import { canWriteKnowledge } from '../../lib/knowledgeAccess.js';
import { knowledgeApi } from './api.js';

/** One General Knowledge file: what it is, what equipment it applies to, and changing that. */
export const useDocumentPageLogic = (bucketId: string, documentId: string) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  const canWrite = me.data ? canWriteKnowledge(me.data.user) : false;

  const doc = useQuery({
    queryKey: ['knowledge', 'document', documentId],
    queryFn: () => knowledgeApi.document(documentId),
  });
  const subjects = useQuery({
    queryKey: ['knowledge', 'subjects'],
    queryFn: knowledgeApi.subjects,
  });
  const buckets = useQuery({ queryKey: ['knowledge', 'buckets'], queryFn: knowledgeApi.buckets });
  const bucket = (buckets.data ?? []).find((b) => b.id === bucketId);

  // The picker edits a draft; closing it saves when the selection changed.
  const [picking, setPicking] = useState(false);
  const [draft, setDraft] = useState<string[]>([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const tagged = (doc.data?.subjects ?? []).map((s) => s.id);

  const save = useMutation({
    mutationFn: (subjectIds: string[]) => knowledgeApi.setDocumentSubjects(documentId, subjectIds),
    onSuccess: (updated) => {
      qc.setQueryData(['knowledge', 'document', documentId], updated);
      void qc.invalidateQueries({ queryKey: ['knowledge', 'generalDocuments'] });
    },
  });
  const remove = useMutation({
    mutationFn: () => knowledgeApi.deleteDocument(documentId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['knowledge', 'generalDocuments'] });
      void navigate(`/knowledge/buckets/${bucketId}`);
    },
  });

  const openPicker = () => {
    setDraft(tagged);
    setPicking(true);
  };
  const closePicker = () => {
    setPicking(false);
    const changed = draft.length !== tagged.length || draft.some((id) => !tagged.includes(id));
    if (changed) save.mutate(draft);
  };

  return {
    doc,
    subjects,
    bucket,
    canWrite,
    picking,
    draft,
    setDraft,
    openPicker,
    closePicker,
    save,
    remove,
    confirmDelete,
    setConfirmDelete,
  };
};
